/**
 * Salon Locator
 * @description Manages client request to block users from searching for salon
 * locations for more than 10 requests
 */

/**
 * Config object
 */
const salonLocator = () => {
  const config = {
    dom: {
      salonLocatorLoader: "salon-locator__loader",
      searchAutoComplete: "pac-container",
      searchAutoCompleteItem: "pac-item",
      searchError: "stockist-error-message",
      searchForm: "stockist-search-form",
      searchFormButton: "stockist-search-button",
      searchInput: "stockist-search-field",
      searchMap: "stockist-map",
      searchSidePanel: "stockist-side-panel",
      searchWidget: "stockist-widget",
    },
    event: {
      change: "change",
      click: "click",
      focusOut: "focusout",
      load: "load",
    },
    cookie: {
      searchCount: "search_count",
      searchCountLimit: 10,
      searchExpiration: "search_expiration",
      setSearchCountThirtyMinuteExpire: 30,
      setSearchCountToOne: 1,
    },
    style: {
      none: "none",
      opacity: "0.9",
    },
    element: {
      div: "div",
      button: "button",
    },
    attribute: {
      disabled: "disabled",
    },
  };

  /**
   * Set Expiration Thirty Minutes From Search
   * @function setExpirationThirtyMinutesFromSearch
   * @description Updates current time to 30 minutes in the future
   * @returns string of updated Date
   */
  const setExpirationThirtyMinutesFromSearch = () => {
    const currentDate = new Date();

    currentDate.setTime(
      currentDate.getTime() +
        config.cookie.setSearchCountThirtyMinuteExpire * 60 * 1000
    );

    return currentDate.toUTCString();
  };

  /**
   * Set Search Count Cookie
   * @param {boolean} fromMutation modifier use to set cookie value for count
   * @function setSearchCountCookie
   * @description Set search count cookie to 1 and expires 30 minutes from creation
   */
  const setSearchCountCookie = (fromMutation) => {
    const setCookieValue = fromMutation
      ? config.cookie.setSearchCountToOne
      : config.cookie.setSearchCountToOne + config.cookie.setSearchCountToOne;

    document.cookie = `${
      config.cookie.searchCount
    }=${setCookieValue}; expires=${setExpirationThirtyMinutesFromSearch()};`;

    setExpirationDateOfFirstSearchCountCookie();
  };

  /**
   * Set Expiration Date Of First Search Count Cookie
   * @function setExpirationDateOfFirstSearchCountCookie
   * @description Creates cookie with 30 minute expiration
   */
  const setExpirationDateOfFirstSearchCountCookie = () => {
    document.cookie = `${
      config.cookie.searchExpiration
    }=${setExpirationThirtyMinutesFromSearch()}; expires=${setExpirationThirtyMinutesFromSearch()};`;
  };

  /**
   * Get Search Count Cookie
   * @function getSearchCountCookie
   * @description Gets search count cookie and returns boolean
   * @returns Boolean
   */
  const getSearchCountCookie = () => {
    return document.cookie
      .split(";")
      .some((cookie) => cookie.trim().startsWith(config.cookie.searchCount));
  };

  /**
   * Get Cookie Value
   * @param {string} key cookie key
   * @functiongetCookieValue
   * @description Gets value of search count cookie
   * @returns String value of search count cookie
   */
  const getCookieValue = (key) => {
    return document.cookie
      .split(";")
      .filter((cookie) => cookie.trim().startsWith(key))
      .join()
      .split("=")[config.cookie.setSearchCountToOne];
  };

  /**
   * Update Search Count Cookie
   * @param {number} currentSearchCountCookieValue
   * @function updateSearchCountCookie
   * @description Updates search count cookie value while preserving expiration date
   */
  const updateSearchCountCookie = (currentSearchCountCookieValue) => {
    const currentSearchCountCookieExpiration = new Date(
      getCookieValue(config.cookie.searchExpiration)
    );

    document.cookie = `${config.cookie.searchCount}=${
      currentSearchCountCookieValue + config.cookie.setSearchCountToOne
    }; expires=${currentSearchCountCookieExpiration.toUTCString()}`;
  };

  /**
   * Handle Search Count Cookie
   * @function handleSearchCountCookie
   * @description Handles search count cookie based on number of search requests
   */
  const handleSearchCountCookie = () => {
    const currentSearchCountCookieValue = Number(
      getCookieValue(config.cookie.searchCount)
    );

    if (currentSearchCountCookieValue >= config.cookie.searchCountLimit) {
      setSearchWidgetDisabledState();
    } else {
      updateSearchCountCookie(currentSearchCountCookieValue);
    }
  };

  /**
   * Add Event Listeners
   * @function addEventListeners
   * @description Adds event listeners to DOM
   */
  const addEventListeners = () => {
    const $searchForm = document.querySelector(`.${config.dom.searchForm}`);
    const $searchInput = document.querySelector(`.${config.dom.searchInput}`);

    $searchForm?.addEventListener(config.event.change, () => {
      getSearchCountCookie()
        ? handleSearchCountCookie()
        : setSearchCountCookie();
    });

    $searchInput?.addEventListener(config.event.focusOut, (event) => {
      event.target.value = "";

      const $searchAutoCompleteItems = document.querySelectorAll(
        `.${config.dom.searchAutoCompleteItem}`
      );

      if (!$searchAutoCompleteItems.length) return;

      $searchAutoCompleteItems?.forEach((element) => {
        element.remove();
      });
    });
  };

  /**
   * Set Search Widget Disabled State
   * @function setSearchWidgetDisabledState
   * @description Sets necesary HTML elements to disabled
   */
  const setSearchWidgetDisabledState = () => {
    const $node = document.createElement(config.element.div);
    const translationText =
      window.salonLocatorTranslations[
        window.localization.country_iso_code === "US"
          ? "en"
          : window.localization.country_iso_code.toLowerCase()
      ];

    const $textNode = document.createTextNode(translationText);

    $node.appendChild($textNode);
    $node.classList.add(config.dom.searchError);

    const $searchSidePanel = document.querySelector(
      `.${config.dom.searchSidePanel}`
    );
    const $searchMap = document.querySelector(`.${config.dom.searchMap}`);
    const $searchInput = document.querySelector(`.${config.dom.searchInput}`);
    const $searchFormButton = document.querySelector(
      `.${config.dom.searchFormButton} ${config.element.button}`
    );

    $searchInput.setAttribute(config.attribute.disabled, true);
    $searchFormButton.setAttribute(config.attribute.disabled, true);
    $searchSidePanel.appendChild($node);
    $searchSidePanel.style.pointerEvents = config.style.none;
    $searchMap.style.pointerEvents = config.style.none;
  };

  /**
   * Handle Page Load Search Cookie State
   * @function handlePageLoadSearchCookieState
   * @description Manages widget state on page load. setTimeout is needed to ensure
   * the whole web page and Stockist app are loaded before we impart additional
   * mutations
   */
  const handlePageLoadSearchCookieState = () => {
    const currentSearchCountCookieValue = Number(
      getCookieValue(config.cookie.searchCount)
    );

    if (currentSearchCountCookieValue >= config.cookie.searchCountLimit) {
      setTimeout(() => {
        setSearchWidgetDisabledState();
        hideSalonLocatorLoader();
      }, 1000);
    } else {
      hideSalonLocatorLoader();
      addEventListeners();
      handleMutationObserver();
    }
  };

  /**
   * Hide Salon Locator Loader
   * @function hideSalonLocatorLoader
   * @description Hides salon locator loader
   */
  const hideSalonLocatorLoader = () => {
    const $salonLocatorLoader = document.querySelector(
      `.${config.dom.salonLocatorLoader}`
    );

    $salonLocatorLoader.style.display = config.style.none;
  };

  /**
   * Handle Event Listener For Mutation Observer
   * @function handleEventListenerForMutationObserver
   * @description Manages callback for forEach autoCompleteElement, adds event
   * listeners based on param
   * @param {string} event event type
   * @param {DOM element} mutationElement
   */
  const handleEventListenerForMutationObserver = (event, mutationElement) => {
    mutationElement.addEventListener(event, () => {
      getSearchCountCookie()
        ? handleSearchCountCookie()
        : setSearchCountCookie(true);
    });
  };

  /**
   * Handle Mutation Observer
   * @function handleMutationObserver
   * @description Uses Mutation Observer class object to watch updates to HTML for
   * search auto complete updates. Observer adds event listeners to updated HTML
   * as auto complete suggestions update.
   */
  const handleMutationObserver = () => {
    const $targetNode = document.querySelector(
      `.${config.dom.searchAutoComplete}`
    );

    const mutationConfig = { childList: true, subtree: true };

    const callback = (mutationList) => {
      mutationList.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          const { children: $autoCompleteList } = mutation.target.children[0];

          [...$autoCompleteList].forEach((autoCompleteElement) => {
            handleEventListenerForMutationObserver(
              config.event.change,
              autoCompleteElement
            );
            handleEventListenerForMutationObserver(
              config.event.click,
              autoCompleteElement
            );
          });
        }
      });
    };

    const observer = new MutationObserver(callback);

    observer.observe($targetNode, mutationConfig);
  };

  /**
   * Init
   */
  const init = () => {
    handlePageLoadSearchCookieState();
  };

  /**
   * Waits for window to fully load before init()
   */
  window.addEventListener(config.event.load, () => {
    let stockistLoading = true;

    while (stockistLoading) {
      if (window.Stockist.loaded) {
        init();
        stockistLoading = false;
      }
    }
  });
};

salonLocator();
